/**
 * A labelled `<select>` (CLAUDE.md 14.5).
 *
 * `Field` + `FieldLabel` wire a label to a control through `useFieldControlProps`,
 * which only `FieldInput` spreads. A bare `<select>` dropped inside a `Field`
 * therefore gets a label pointing at an id that does not exist -- it *looks*
 * associated and is not, which is worse than an obviously unlabelled control because
 * nothing on screen shows the defect.
 *
 * So: an explicit id, a real `htmlFor`, and the same 44px target as every other
 * control (14.3). Native `<select>` rather than a Radix listbox because the native
 * one already has correct keyboard behaviour, works on a phone, and carries no
 * measurement cost.
 */
import { useId, type ReactNode, type SelectHTMLAttributes } from 'react'

import { cn } from '@/lib/cn'

export interface SelectFieldProps extends Omit<SelectHTMLAttributes<HTMLSelectElement>, 'id'> {
  label: ReactNode
  /** Shown under the control, and referenced by `aria-describedby`. */
  description?: ReactNode
  /** Hides the label visually while keeping it for assistive technology. */
  labelHidden?: boolean
}

export function SelectField({
  label,
  description,
  labelHidden = false,
  className,
  children,
  ...props
}: SelectFieldProps) {
  const id = useId()
  const descriptionId = `${id}-description`

  return (
    <div className="space-y-1.5">
      <label htmlFor={id} className={cn('block text-sm font-medium', labelHidden && 'sr-only')}>
        {label}
      </label>
      <select
        id={id}
        aria-describedby={description ? descriptionId : undefined}
        className={cn(
          'h-11 w-full rounded-md border border-border bg-surface px-3 text-sm text-text',
          'focus-visible:outline-2 focus-visible:outline-offset-1 disabled:opacity-50',
          className,
        )}
        {...props}
      >
        {children}
      </select>
      {description && (
        <p id={descriptionId} className="text-xs text-text-muted">
          {description}
        </p>
      )}
    </div>
  )
}
